<template>
  <teleport :to="container">
    <transition name="fade" @after-leave="onAfterLeave">
      <div
        v-show="modelValue"
        v-bind="$attrs"
        ref="modalRef"
        class="lp-modal-mask"
        data-ua-unique-id="unique-modal"
        role="presentation"
        :style="{ cursor: closableMask ? 'pointer' : 'default' }"
        :class="[priorityPosition && 'lp-modal-mask--priority-position']"
      >
        <transition name="drop-top">
          <div v-show="modelValue" class="lp-modal-wrap">
            <div
              class="lp-modal-wrap-inner"
              tabindex="0"
              data-testid="modal-mask"
              @click.self="maskClick"
            >
              <div
                ref="modalBody"
                class="lp-modal-body"
                :class="[
                  size ? `modal-${size}` : '',
                  onlyImageSlot ? 'lp-modal-body-has-image' : '',
                ]"
              >
                <!-- <slot name="image" />
                <slot /> -->
                <div
                  v-if="itHasHeader"
                  class="lp-modal-header ly-row ly-spacebetween-start"
                  :class="
                    isScrolledTop && 'shadow-lg border-bottom border-gray-light'
                  "
                >
                  <slot name="header" />
                  <lp-icon
                    v-if="closable"
                    name="close"
                    class="mt-half cursor-pointer"
                    role="button"
                    @click="close()"
                  />
                </div>
                <div
                  v-if="itHasBody"
                  ref="bodyRef"
                  class="lp-modal-content"
                  :class="[
                    compact && 'compact',
                    !autoHeight && 'lp-modal-content-min-height',
                    autoHeight && 'lp-modal-content-auto-height',
                  ]"
                  @scroll="calculateOffset"
                >
                  <slot name="body" />
                </div>
                <div
                  v-if="itHasActions"
                  class="lp-modal-footer"
                  :class="
                    canScroll() &&
                    !isScrolledBottom &&
                    'shadow-lg-inner border-top border-gray-light'
                  "
                >
                  <slot name="actions" />
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import {
  defineComponent,
  watch,
  ref,
  computed,
  nextTick,
  getCurrentInstance,
  PropType,
  watchEffect,
} from 'vue'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { useCheckSlot } from '@/hooks'

export type Sizes = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export default defineComponent({
  name: 'lp-modal',
  inheritAttrs: false,
  props: {
    size: { type: String as PropType<Sizes>, default: 'md' },
    container: { type: String, default: 'body' },
    modelValue: { type: Boolean, default: false },
    minHeight: { type: String, default: '500px' },
    closableMask: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    closable: { type: Boolean, default: true },
    priorityPosition: { type: Boolean },
    autoHeight: { type: Boolean, default: false },
    withoutFocusTrap: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'modal-close', 'after-close-animation'],
  setup(props, { emit, slots }) {
    const modalRef = ref<HTMLElement | null>()
    const itHasHeader = useCheckSlot(slots, 'header') !== null
    const itHasBody = useCheckSlot(slots, 'body') !== null
    const itHasActions = useCheckSlot(slots, 'actions') !== null
    const itHasImage = useCheckSlot(slots, 'image') !== null

    const modalBody = ref<HTMLElement | null>()
    const bodyRef = ref<HTMLElement | null>()

    const bodyRefScrollHeight = ref(0)
    const bodyRefScrollTop = ref(0)
    const bodyRefClientHeight = ref(0)

    const isScrolledTop = ref(canScroll())
    const isScrolledBottom = ref(canScroll())
    const LpComponents = getCurrentInstance()

    const { hasFocus, activate, deactivate } = useFocusTrap(modalRef)

    function close() {
      emit('update:modelValue', false)
      emit('modal-close', true)
    }

    function maskClick() {
      if (props.closableMask) {
        close()
      }
    }

    function pressEsc(e: KeyboardEvent) {
      e.code === 'Escape' && close()
    }

    watch(
      () => props.modelValue,
      async (active: boolean) => {
        await nextTick()
        const modalsList =
          LpComponents!.appContext.config.globalProperties.$LpComponents.drawers
        if (modalRef.value) {
          if (active) {
            modalsList.push(modalBody.value)

            modalsList
              .slice(0, modalsList.indexOf(modalBody.value))
              .forEach((modalEl: HTMLElement) => {
                if (modalEl.style.transform !== '') {
                  const scaleAndTranslate = modalEl.style.transform
                    .match(/[-+]?[0-9]*\.?[0-9]+/g)
                    ?.map((ittt) => parseFloat(ittt))
                  modalEl.style.transform = `scale(${
                    scaleAndTranslate![0] - 0.05
                  }) translateY(${scaleAndTranslate![1] - 5}%)`
                } else {
                  modalEl.style.transform = `scale(0.9) translateY(5%)`
                }
              })

            disableBodyScroll(modalRef.value, { reserveScrollBarGap: true })
            if (props.closeOnEsc) {
              document.addEventListener('keydown', pressEsc)
            }

            if (!props.withoutFocusTrap) {
              activate()
            }
          } else {
            modalsList.splice(modalsList.indexOf(modalBody.value), 1)

            modalsList.forEach((modalEl: HTMLElement, i: number) => {
              if (i === modalsList.length - 1) {
                modalEl.style.transform = ''
                return
              }
              const scaleAndTranslate = modalEl.style.transform
                .match(/[-+]?[0-9]*\.?[0-9]+/g)
                ?.map((val) => parseFloat(val))
              modalEl.style.transform = `scale(${
                scaleAndTranslate![0] + 0.05
              }) translateY(${scaleAndTranslate![1] + 5}%)`
            })

            if (modalBody.value) {
              modalBody.value.style.transform = ''
            }

            if (!props.withoutFocusTrap) {
              deactivate()
            }
            setTimeout(enableBodyScroll.bind(this, modalRef.value), 500)
            document.removeEventListener('keydown', pressEsc)
          }
        }
      },
    )

    const onlyImageSlot = computed(() => {
      return itHasImage && !itHasHeader && !itHasBody && !itHasActions
    })

    function onAfterLeave(el: Element) {
      emit('after-close-animation', el)
    }

    function calculateOffset() {
      if (!bodyRef.value) {
        return
      }
      bodyRefScrollHeight.value = bodyRef.value?.scrollHeight
      bodyRefScrollTop.value = bodyRef.value?.scrollTop
      bodyRefClientHeight.value = bodyRef.value?.clientHeight
      isScrolledBottom.value = canScroll()
        ? Math.abs(
            bodyRefScrollHeight.value -
              bodyRefClientHeight.value -
              bodyRefScrollTop.value,
          ) <= 1
        : false
      isScrolledTop.value = bodyRefScrollTop.value !== 0
    }

    watchEffect(() => {
      bodyRefScrollHeight.value = bodyRef.value?.scrollHeight
      bodyRefClientHeight.value = bodyRef.value?.clientHeight
      canScroll() && !isScrolledBottom.value
    })

    function canScroll() {
      nextTick().then(() => {
        bodyRefScrollHeight.value = bodyRef.value?.scrollHeight
        bodyRefClientHeight.value = bodyRef.value?.clientHeight
      })
      return bodyRefScrollHeight.value > bodyRefClientHeight.value
    }

    return {
      modalRef,
      modalBody,
      maskClick,
      hasFocus,
      itHasHeader,
      itHasBody,
      itHasActions,
      close,
      onlyImageSlot,
      onAfterLeave,
      isScrolledTop,
      isScrolledBottom,
      bodyRef,
      bodyRefScrollHeight,
      bodyRefScrollTop,
      bodyRefClientHeight,
      calculateOffset,
      canScroll,
    }
  },
})
</script>
<style>
.lp-modal-content-min-height {
  min-height: v-bind(minHeight);
}
</style>
